import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper,
  alpha,
  Chip,
} from '@mui/material';
import { parseInvoiceDesignSettings } from '../utils/invoiceDesignDefaults';
import { numberToWords } from '../utils/numberToWords';
import InvoiceBarcode from './InvoiceBarcode';
import { formatItemDisplayName } from '../../modules/sales/utils/formatItemDisplayName';

export function CustomDocumentRenderer({
  docType = 'invoice', // 'invoice' | 'quotation'
  order,
  quotation,
  company,
  designSettings,
  currency: propCurrency,
  dateFormat: propDateFormat,
  itemFormat: propItemFormat,
}) {
  const doc = docType === 'invoice' ? (order || {}) : (quotation || {});
  const design = parseInvoiceDesignSettings(designSettings || company?.invoiceDesignSettings);

  const currency = propCurrency || company?.currency || 'USD';
  const decimals = company?.currencyDecimals ?? 2;
  const dateFormat = propDateFormat || company?.dateFormat || 'dd/MM/yyyy';
  const itemFormat = propItemFormat || company?.printItemNameFormat || 'name_sku';

  const formatAmount = (v) => {
    const amt = Number(v ?? 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(amt);
    } catch {
      return `${currency} ${amt.toFixed(decimals)}`;
    }
  };

  const formatDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy': return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd': return `${yyyy}-${mm}-${dd}`;
      case 'dd-MM-yyyy': return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default: return `${dd}/${mm}/${yyyy}`;
    }
  };

  const theme = design.theme || 'modern';
  const primaryColor = design.primaryColor || '#0ea5e9';
  const secondaryColor = design.secondaryColor || '#f8fafc';
  const density = design.density || 'normal';

  const cellPadding = density === 'compact' ? '4px 6px' : density === 'spacious' ? '10px 14px' : '6px 10px';
  const fontSizeBase = density === 'compact' ? '0.78rem' : density === 'spacious' ? '0.92rem' : '0.84rem';

  const isInvoice = docType === 'invoice';
  const docTitle = isInvoice
    ? (design.invoiceTitle || 'TAX INVOICE')
    : (design.quotationTitle || 'PRICE QUOTATION');

  const docNumber = isInvoice
    ? (doc.invoiceNumber || doc.orderNumber || 'INV-0001')
    : (doc.quotationNumber || 'QT-0001');

  const docDate = isInvoice
    ? (doc.orderDate || doc.createdAt || new Date())
    : (doc.quotationDate || doc.createdAt || new Date());

  const dueDate = isInvoice ? doc.dueDate : doc.validUntil;

  const items = doc.items || [];
  const customer = doc.customer || {};

  const salesPersonObj = doc.salesPerson || doc.salesperson || doc.user || doc.creator;
  const salesPersonName = typeof salesPersonObj === 'string'
    ? salesPersonObj
    : (salesPersonObj
        ? `${salesPersonObj.firstName || ''} ${salesPersonObj.lastName || ''}`.trim() || salesPersonObj.username || salesPersonObj.name || salesPersonObj.email
        : (doc.salesPersonName || doc.salespersonName || ''));
  const salesPersonEmail = typeof salesPersonObj === 'object' ? salesPersonObj?.email : '';
  const salesPersonPhone = typeof salesPersonObj === 'object' ? (salesPersonObj?.phone || salesPersonObj?.mobile) : '';
  const salespersonLabel = design.salespersonLabel || 'Salesperson';

  const grandTotal = Number(doc.totalAmount ?? doc.grandTotal ?? 0);
  const subtotal = Number(doc.subtotal ?? (grandTotal - Number(doc.vatTotal || 0) + Number(doc.discountTotal || 0)));
  const discountTotal = Number(doc.discountTotal || 0);
  const vatTotal = Number(doc.vatTotal || 0);

  const cols = design.columns || {};
  const labels = design.columnLabels || {};

  return (
    <Box
      sx={{
        fontFamily: design.fontFamily || 'Inter, sans-serif',
        fontSize: fontSizeBase,
        color: design.textColor || '#0f172a',
        bgcolor: '#ffffff',
        p: density === 'compact' ? 2.5 : density === 'spacious' ? 4.5 : 3.5,
        borderRadius: `${design.borderRadius || 8}px`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        border: theme === 'grid' ? `2px solid ${primaryColor}` : '1px solid #e2e8f0',
        maxWidth: 900,
        margin: '0 auto',
        '@media print': {
          boxShadow: 'none',
          border: 'none',
          p: 1,
          maxWidth: '100%',
        },
      }}
    >
      {/* ─── Top Header & Branding ────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          pb: 2.5,
          borderBottom: theme === 'minimal' ? '1px solid #e2e8f0' : `3px solid ${primaryColor}`,
          mb: 2.5,
        }}
      >
        {/* Left: Company Logo & Info */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
          {design.showLogo && company?.logoUrl && (
            <Box
              component="img"
              src={company.logoUrl}
              alt="Logo"
              sx={{
                width: design.logoWidth || 130,
                maxHeight: 75,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
          <Box>
            {design.showCompanyNameEn && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: primaryColor,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {company?.name || 'Your Company Name'}
              </Typography>
            )}
            {design.showCompanyNameAr && company?.nameArabic && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.secondary', direction: 'rtl', mt: 0.3 }}
              >
                {company.nameArabic}
              </Typography>
            )}
            <Box sx={{ mt: 0.6, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.4 }}>
              {design.showCompanyAddress && company?.address && <div>{company.address}</div>}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.3 }}>
                {design.showCompanyPhone && company?.phone && <div>📞 {company.phone}</div>}
                {design.showCompanyEmail && company?.email && <div>✉️ {company.email}</div>}
                {design.showCompanyWebsite && company?.website && <div>🌐 {company.website}</div>}
              </Box>
              {design.showCompanyTaxId && (company?.taxId || company?.vatNumber) && (
                <div style={{ fontWeight: 600, color: '#334155', marginTop: '2px' }}>
                  {company?.taxLabel || 'TRN / VAT'}: {company?.taxId || company?.vatNumber}
                </div>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right: Document Badge & Meta */}
        <Box sx={{ textAlign: 'right', minWidth: 200 }}>
          <Box
            sx={{
              display: 'inline-block',
              bgcolor: theme === 'minimal' ? '#f1f5f9' : primaryColor,
              color: theme === 'minimal' ? primaryColor : '#ffffff',
              px: 2,
              py: 0.6,
              borderRadius: 1.5,
              fontWeight: 800,
              fontSize: '1.05rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            {docTitle}
          </Box>

          <Box sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.6 }}>
            {design.showDocNumber && (
              <div>
                <strong>{isInvoice ? 'Invoice No:' : 'Quotation No:'}</strong>{' '}
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                  {docNumber}
                </span>
              </div>
            )}
            {design.showDocDate && (
              <div>
                <strong>Date:</strong> {formatDate(docDate)}
              </div>
            )}
            {design.showDueDate && dueDate && (
              <div>
                <strong>{isInvoice ? 'Due Date:' : 'Valid Until:'}</strong> {formatDate(dueDate)}
              </div>
            )}
            {design.showPaymentTerms && doc.paymentTerms && (
              <div>
                <strong>Terms:</strong> {doc.paymentTerms}
              </div>
            )}
            {design.showSalesperson && (!design.salespersonPosition || design.salespersonPosition === 'header') && salesPersonName && (
              <div>
                <strong>{salespersonLabel}:</strong> {salesPersonName}
                {design.showSalespersonEmail && salesPersonEmail && (
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{salesPersonEmail}</div>
                )}
                {design.showSalespersonPhone && salesPersonPhone && (
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{salesPersonPhone}</div>
                )}
              </div>
            )}
            {design.showBarcode && docNumber && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.8 }}>
                <InvoiceBarcode value={docNumber} height={26} width={1.2} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ─── Customer / Bill To ────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: theme === 'classic' ? '#fdfdfd' : secondaryColor,
          p: 1.8,
          borderRadius: 1.5,
          border: '1px solid #e2e8f0',
          mb: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: primaryColor,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'block',
              mb: 0.4,
            }}
          >
            {isInvoice ? 'Billed To / Customer:' : 'Quotation For / Client:'}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {customer.name || doc.customerName || 'Cash / Retail Customer'}
          </Typography>
          {customer.nameArabic && (
            <Typography variant="body2" sx={{ color: 'text.secondary', direction: 'rtl' }}>
              {customer.nameArabic}
            </Typography>
          )}
          <Box sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.4, lineHeight: 1.4 }}>
            {design.showCustomerAddress && customer.address && <div>{customer.address}</div>}
            {design.showCustomerPhone && customer.phone && <div>📞 {customer.phone}</div>}
            {design.showCustomerEmail && customer.email && <div>✉️ {customer.email}</div>}
            {design.showCustomerTaxId && (customer.taxId || customer.trn) && (
              <div style={{ fontWeight: 600, color: '#334155', marginTop: '2px' }}>
                Customer TRN/Tax ID: {customer.taxId || customer.trn}
              </div>
            )}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right', minWidth: 160, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {doc.referenceNumber && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block' }}>
                Reference / PO No:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {doc.referenceNumber}
              </Typography>
            </Box>
          )}

          {design.showSalesperson && design.salespersonPosition === 'customer' && salesPersonName && (
            <Box sx={{ bgcolor: '#ffffff', p: 1, borderRadius: 1, border: '1px solid #e2e8f0', textAlign: 'left' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                {salespersonLabel}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {salesPersonName}
              </Typography>
              {design.showSalespersonEmail && salesPersonEmail && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  ✉️ {salesPersonEmail}
                </Typography>
              )}
              {design.showSalespersonPhone && salesPersonPhone && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  📞 {salesPersonPhone}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── Line Items Table ─────────────────────────────────────────────────── */}
      <Box sx={{ overflowX: 'auto', mb: 2.5 }}>
        <Table
          size="small"
          sx={{
            minWidth: 600,
            borderCollapse: 'collapse',
            '& th': {
              bgcolor: theme === 'minimal' ? '#f8fafc' : primaryColor,
              color: theme === 'minimal' ? '#0f172a' : '#ffffff',
              fontWeight: 700,
              fontSize: fontSizeBase,
              p: cellPadding,
              border: theme === 'grid' ? '1px solid #cbd5e1' : 'none',
            },
            '& td': {
              p: cellPadding,
              fontSize: fontSizeBase,
              borderBottom: '1px solid #e2e8f0',
              borderRight: theme === 'grid' ? '1px solid #cbd5e1' : 'none',
              borderLeft: theme === 'grid' ? '1px solid #cbd5e1' : 'none',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {cols.srNo && <TableCell sx={{ width: 40, textAlign: 'center' }}>{labels.srNo || '#'}</TableCell>}
              {cols.sku && <TableCell sx={{ width: 95 }}>{labels.sku || 'Code'}</TableCell>}
              {cols.barcode && <TableCell sx={{ width: 100 }}>{labels.barcode || 'Barcode'}</TableCell>}
              {cols.description && <TableCell>{labels.description || 'Description'}</TableCell>}
              {cols.unit && <TableCell sx={{ width: 65, textAlign: 'center' }}>{labels.unit || 'Unit'}</TableCell>}
              {cols.quantity && <TableCell sx={{ width: 65, textAlign: 'right' }}>{labels.quantity || 'Qty'}</TableCell>}
              {cols.unitPrice && <TableCell sx={{ width: 85, textAlign: 'right' }}>{labels.unitPrice || 'Price'}</TableCell>}
              {cols.discount && <TableCell sx={{ width: 75, textAlign: 'right' }}>{labels.discount || 'Disc.'}</TableCell>}
              {cols.taxRate && <TableCell sx={{ width: 75, textAlign: 'right' }}>{labels.taxRate || 'VAT %'}</TableCell>}
              {cols.taxAmount && <TableCell sx={{ width: 80, textAlign: 'right' }}>Tax Amt</TableCell>}
              {cols.total && <TableCell sx={{ width: 95, textAlign: 'right' }}>{labels.total || 'Total'}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  No items listed
                </TableCell>
              </TableRow>
            ) : (
              items.map((it, idx) => {
                const prod = it.product || {};
                const name = formatItemDisplayName(
                  {
                    name: prod.name || it.productName || it.name,
                    sku: prod.sku || it.productSku || it.sku,
                    brand: prod.brand || it.productBrand || it.brand,
                    description: prod.description || it.description || it.notes,
                  },
                  itemFormat
                );
                const qty = Number(it.quantity || 1);
                const unitPrice = Number(it.unitPrice ?? it.price ?? 0);
                const discAmt = Number(it.discount ?? it.discountAmount ?? 0);
                const taxRate = Number(it.taxRate ?? it.vatRate ?? 0);
                const taxAmt = Number(it.taxAmount ?? it.vatAmount ?? ((unitPrice * qty - discAmt) * (taxRate / 100)));
                const rowTotal = Number(it.total ?? it.totalAmount ?? (unitPrice * qty - discAmt + (company?.salesVatIncluded ? 0 : taxAmt)));

                return (
                  <TableRow key={it.id || idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#fbfcfd' } }}>
                    {cols.srNo && <TableCell sx={{ textAlign: 'center', color: '#64748b' }}>{idx + 1}</TableCell>}
                    {cols.sku && <TableCell sx={{ fontFamily: 'monospace', color: '#334155' }}>{prod.sku || it.sku || '—'}</TableCell>}
                    {cols.barcode && <TableCell sx={{ fontFamily: 'monospace' }}>{prod.barcode || it.barcode || '—'}</TableCell>}
                    {cols.description && (
                      <TableCell>
                        <div style={{ fontWeight: 600 }}>{name}</div>
                        {cols.warranty && it.hasWarranty && it.warrantyPeriod && (
                          <span style={{ display: 'inline-block', fontSize: '0.75rem', color: '#16a34a', marginTop: '1px' }}>
                            🛡️ Warranty: {it.warrantyPeriod.name}
                          </span>
                        )}
                        {cols.serialNo && it.serialNumbers && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1px' }}>
                            SN: {Array.isArray(it.serialNumbers) ? it.serialNumbers.join(', ') : it.serialNumbers}
                          </div>
                        )}
                      </TableCell>
                    )}
                    {cols.unit && <TableCell sx={{ textAlign: 'center', color: '#64748b' }}>{prod.unit || it.unit || 'Pcs'}</TableCell>}
                    {cols.quantity && <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>{qty}</TableCell>}
                    {cols.unitPrice && <TableCell sx={{ textAlign: 'right' }}>{formatAmount(unitPrice)}</TableCell>}
                    {cols.discount && <TableCell sx={{ textAlign: 'right', color: discAmt > 0 ? '#dc2626' : '#64748b' }}>{discAmt > 0 ? formatAmount(discAmt) : '—'}</TableCell>}
                    {cols.taxRate && <TableCell sx={{ textAlign: 'right' }}>{taxRate > 0 ? `${taxRate}%` : '0%'}</TableCell>}
                    {cols.taxAmount && <TableCell sx={{ textAlign: 'right' }}>{formatAmount(taxAmt)}</TableCell>}
                    {cols.total && <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{formatAmount(rowTotal)}</TableCell>}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>

      {/* ─── Bottom Section: Bank / Terms (Left) + Totals (Right) ──────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
        {/* Left Side: Bank Details & Terms */}
        <Box sx={{ flex: 1, minWidth: 260 }}>
          {/* Bank Payment Information */}
          {design.showBankDetails && (design.bankDetails?.bankName || design.bankDetails?.accountNumber || company?.bankAccounts?.length > 0) && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 1.5,
                bgcolor: '#fafafa',
                borderColor: '#e2e8f0',
                fontSize: '0.78rem',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: primaryColor, textTransform: 'uppercase', display: 'block', mb: 0.4 }}>
                🏦 Bank & Payment Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px', color: '#334155' }}>
                {design.bankDetails?.bankName && (
                  <>
                    <span style={{ fontWeight: 600 }}>Bank:</span>
                    <span>{design.bankDetails.bankName}</span>
                  </>
                )}
                {design.bankDetails?.accountName && (
                  <>
                    <span style={{ fontWeight: 600 }}>Account Name:</span>
                    <span>{design.bankDetails.accountName}</span>
                  </>
                )}
                {design.bankDetails?.accountNumber && (
                  <>
                    <span style={{ fontWeight: 600 }}>Account No:</span>
                    <span style={{ fontFamily: 'monospace' }}>{design.bankDetails.accountNumber}</span>
                  </>
                )}
                {design.bankDetails?.iban && (
                  <>
                    <span style={{ fontWeight: 600 }}>IBAN:</span>
                    <span style={{ fontFamily: 'monospace' }}>{design.bankDetails.iban}</span>
                  </>
                )}
                {design.bankDetails?.swift && (
                  <>
                    <span style={{ fontWeight: 600 }}>SWIFT/BIC:</span>
                    <span style={{ fontFamily: 'monospace' }}>{design.bankDetails.swift}</span>
                  </>
                )}
              </Box>
            </Paper>
          )}

          {/* Terms and Conditions */}
          {(isInvoice ? design.invoiceTerms : design.quotationTerms) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', display: 'block', mb: 0.3 }}>
                Terms & Conditions:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: '#64748b',
                  whiteSpace: 'pre-line',
                  lineHeight: 1.4,
                }}
              >
                {isInvoice ? design.invoiceTerms : design.quotationTerms}
              </Typography>
            </Box>
          )}

          {/* Default Notes */}
          {design.defaultNotes && (
            <Typography variant="body2" sx={{ fontSize: '0.78rem', fontStyle: 'italic', color: '#64748b' }}>
              “{design.defaultNotes}”
            </Typography>
          )}
        </Box>

        {/* Right Side: Totals Summary */}
        <Box sx={{ minWidth: 260, flex: '0 0 280px' }}>
          <Box
            sx={{
              bgcolor: secondaryColor,
              p: 1.8,
              borderRadius: 1.5,
              border: '1px solid #e2e8f0',
            }}
          >
            {design.showSubtotal && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6, fontSize: '0.82rem' }}>
                <span style={{ color: '#64748b' }}>Subtotal:</span>
                <span style={{ fontWeight: 600 }}>{formatAmount(subtotal)}</span>
              </Box>
            )}

            {design.showTotalDiscount && discountTotal > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6, fontSize: '0.82rem', color: '#dc2626' }}>
                <span>Discount:</span>
                <span>-{formatAmount(discountTotal)}</span>
              </Box>
            )}

            {design.showTaxSummary && vatTotal > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6, fontSize: '0.82rem' }}>
                <span style={{ color: '#64748b' }}>{company?.taxLabel || 'VAT'} Total:</span>
                <span style={{ fontWeight: 600 }}>{formatAmount(vatTotal)}</span>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: primaryColor,
                fontWeight: 800,
                fontSize: '1.1rem',
              }}
            >
              <span>Grand Total:</span>
              <span>{formatAmount(grandTotal)}</span>
            </Box>
          </Box>

          {/* Amount in words */}
          {design.showAmountInWords && grandTotal > 0 && (
            <Box sx={{ mt: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 1, border: '1px dashed #cbd5e1' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block' }}>
                Amount in Words:
              </Typography>
              <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, display: 'block', textTransform: 'capitalize' }}>
                {numberToWords(grandTotal, currency)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── Bottom Salesperson Placement ──────────────────────────────────── */}
      {design.showSalesperson && design.salespersonPosition === 'bottom' && salesPersonName && (
        <Box sx={{ mt: 2.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
              {salespersonLabel}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {salesPersonName}
            </Typography>
          </Box>
          {(design.showSalespersonPhone && salesPersonPhone || design.showSalespersonEmail && salesPersonEmail) && (
            <Box sx={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {design.showSalespersonPhone && salesPersonPhone && <span>📞 {salesPersonPhone}</span>}
              {design.showSalespersonEmail && salesPersonEmail && <span>✉️ {salesPersonEmail}</span>}
            </Box>
          )}
        </Box>
      )}

      {/* ─── Signature Block ─────────────────────────────────────────────────── */}
      {design.showSignatureBox && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            mt: 4,
            pt: 2,
            borderTop: '1px dashed #e2e8f0',
          }}
        >
          {design.showCustomerSignature && (
            <Box sx={{ textAlign: 'center', minWidth: 180 }}>
              <Box sx={{ borderBottom: '1px solid #94a3b8', height: 40, mb: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                {design.customerSignatureLabel || 'Customer Acceptance & Stamp'}
              </Typography>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', minWidth: 180, ml: 'auto' }}>
            <Box sx={{ borderBottom: '1px solid #94a3b8', height: 40, mb: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>
              {design.signatureLabel || 'Authorized Signature & Stamp'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
